export type MapStyleKey =
  | 'streets'
  | 'outdoors'
  | 'light'
  | 'dark'
  | 'satellite'
  | 'satellite-streets'
  | 'navigation-day'
  | 'navigation-night';

export type MapCenterInput =
  | {
      type: 'auto';
    }
  | {
      type: 'manual';
    }
  | {
      type: 'virtualClinic';
      virtualClinicId: string;
    }
  | {
      /**
       * Centres on a clinic's address. Identity is the opaque `clinicSn` plus
       * the optional report `year` (BDA-5804) — the legacy composite
       * `clinicId: 'ik_sn_year'` is no longer written and is converted on read
       * (see `AysCubeDashboardMapTile.migrateMapboxOptions`).
       *
       * `year` is only present when the source identifier carried one; the SN
       * clinic search does not return a per-result report year, so clinics
       * picked today persist without it.
       */
      type: 'clinic';
      clinicSn: string;
      year?: number;
    };

export const MAP_CENTER_INPUT_DISPLAY_NAME: Record<
  'auto' | 'manual' | 'virtualClinic' | 'clinic',
  string
> = {
  auto: 'Automatisch',
  manual: 'Manuell',
  virtualClinic: 'Adresse von virtueller Klinik',
  clinic: 'Adresse von Klinik',
};

export const MAP_STYLE_KEY_DISPLAY_NAME: Record<MapStyleKey, string> = {
  streets: 'Straßen',
  outdoors: 'Outdoor',
  light: 'Hell',
  dark: 'Dunkel',
  satellite: 'Satellit',
  'satellite-streets': 'Satellit & Straßen',
  'navigation-day': 'Navigation Hell',
  'navigation-night': 'Navigation Dunkel',
};

export class MapStyle {
  static fromStyleUrl(styleUrl: string): MapStyleKey {
    if (styleUrl.includes('streets')) {
      return 'streets';
    } else if (styleUrl.includes('outdoors')) {
      return 'outdoors';
    } else if (styleUrl.includes('light')) {
      return 'light';
    } else if (styleUrl.includes('dark')) {
      return 'dark';
    } else if (styleUrl.includes('satellite-streets')) {
      return 'satellite-streets';
    } else if (styleUrl.includes('navigation-day')) {
      return 'navigation-day';
    } else if (styleUrl.includes('navigation-night')) {
      return 'navigation-night';
    } else {
      return 'satellite';
    }
  }

  static fromStyleKey(styleKey: MapStyleKey): string {
    switch (styleKey) {
      case 'streets':
        return 'mapbox://styles/mapbox/streets-v12';
      case 'outdoors':
        return 'mapbox://styles/mapbox/outdoors-v12';
      case 'light':
        return 'mapbox://styles/mapbox/light-v11';
      case 'dark':
        return 'mapbox://styles/mapbox/dark-v11';
      case 'satellite':
        return 'mapbox://styles/mapbox/satellite-v9';
      case 'satellite-streets':
        return 'mapbox://styles/mapbox/satellite-streets-v12';
      case 'navigation-day':
        return 'mapbox://styles/mapbox/navigation-day-v1';
      case 'navigation-night':
        return 'mapbox://styles/mapbox/navigation-night-v1';
      default:
        return 'mapbox://styles/mapbox/streets-v12';
    }
  }
}
